import CountrySelect from "@/components/ui/CountrySelect";
import FlagComponent from "@/components/ui/FlagComponent";
import {Input} from "@/components/ui/input";
import clsxm from "@/lib/clsxm";
import {type ComponentProps, type FC, type RefObject} from "react";
import {useTranslation} from "react-i18next";
import * as RPNInput from "react-phone-number-input";

type PhoneInputProps = Omit<ComponentProps<"input">, "onChange" | "value" | "ref"> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
    showLabel?: boolean;
    numberInputClassName?: string;
    buttonClassName?: string;
    error?: string;
  };

const PhoneInput: FC<PhoneInputProps & {ref?: RefObject<any>}> = ({
  ref,
  className,
  onChange,
  showLabel = true,
  numberInputClassName,
  buttonClassName,
  error,
  ...props
}) => {
  const {t} = useTranslation();

  return (
    <div>
      {showLabel && (
        <label htmlFor="phone-number" className="text-neutral-grey-dark text-sm font-medium">
          {t("Phone number")}
        </label>
      )}
      <RPNInput.default
        ref={ref}
        className={clsxm("flex", className)}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        countrySelectProps={{buttonClassName: buttonClassName, error}}
        inputComponent={InputComponent}
        numberInputProps={{className: numberInputClassName, error}}
        smartCaret={false}
        international
        /**
         * Handles the onChange event.
         *
         * react-phone-number-input might trigger the onChange event as undefined
         * when a valid phone number is not entered. To prevent this,
         * the value is coerced to an empty string.
         *
         * @param {E164Number | undefined} value - The entered value
         */
        onChange={value => onChange?.(value || ("" as RPNInput.Value))}
        {...props}
      />
      {!!error && <span className="text-danger-medium-red mt-0.5 text-xs">{t(error)}</span>}
    </div>
  );
};
PhoneInput.displayName = "PhoneInput";

type InputComponentProps = ComponentProps<"input"> & {
  error?: boolean;
};

const InputComponent: FC<InputComponentProps & {ref?: RefObject<HTMLInputElement>}> = ({
  ref,
  className,
  error,
  ...props
}) => {
  const {t} = useTranslation("company_profile");

  return (
    <Input
      className={clsxm(
        "h-14 rounded-s-none rounded-e-lg focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 md:text-base",
        error && "border-danger-medium-red focus:border-danger-dark-red",
        className,
      )}
      placeholder={t("phone_number_placeholder")}
      {...props}
      ref={ref}
    />
  );
};

export {PhoneInput};
